import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './SubsectionHeader.css';

/**
 * Reusable Subsection Header Component
 * Provides consistent styling for subsections within main sections
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon component to display
 * @param {string} props.title - Subsection title
 * @param {string} props.subtitle - Optional subtitle
 * @param {boolean} props.isExpanded - Whether subsection is expanded
 * @param {Function} props.onToggle - Toggle handler function
 * @param {boolean} props.showToggle - Whether to show toggle button (default: true)
 * @param {string} props.iconAnimation - Icon animation type: 'rotate', 'scale', 'float', 'none' (default: 'rotate')
 */
function SubsectionHeader({ 
  icon, 
  title, 
  subtitle, 
  isExpanded, 
  onToggle, 
  showToggle = true,
  iconAnimation = 'rotate'
}) {
  // Icon animation variants
  const iconAnimations = {
    rotate: {
      rotate: [0, 15, -15, 0],
    },
    scale: {
      scale: [1, 1.1, 1],
    },
    float: {
      y: [0, -8, 0],
    },
    none: {}
  };

  const iconAnimationProps = iconAnimations[iconAnimation] || iconAnimations.rotate;

  // Force apply colors via style tag if CSS isn't working
  useEffect(() => {
    const styleId = 'subsection-header-force-colors';
    // Remove existing style if it exists to update it
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
        .subsection-header-creative .subsection-title-wrapper h3.subsection-title,
        .subsection-header-creative .subsection-title-wrapper h3,
        .subsection-header-creative h3.subsection-title,
        .subsection-header-creative h3,
        .subsection-header-creative .subsection-title,
        .kick-counter-container .subsection-header-creative .subsection-title-wrapper > div > h3.subsection-title,
        .kick-counter-container .subsection-header-creative .subsection-title-wrapper h3.subsection-title,
        .kick-counter-container .subsection-header-creative .subsection-title-wrapper h3,
        .kick-counter-container .subsection-header-creative h3.subsection-title,
        .kick-counter-container .subsection-header-creative h3,
        .kick-counter-container .subsection-header-creative .subsection-title,
        .section-content .kick-counter-container .subsection-header-creative h3.subsection-title,
        .section-content .kick-counter-container .subsection-header-creative h3,
        .summary-section .kick-counter-container .subsection-header-creative h3.subsection-title,
        .summary-section .kick-counter-container .subsection-header-creative h3,
        .kick-counter-container h3.subsection-title,
        .kick-section .subsection-header-creative h3.subsection-title,
        .kick-section .subsection-header-creative h3,
        .kick-section .subsection-header-creative .subsection-title,
        .section-content .kick-section .subsection-header-creative h3.subsection-title,
        .section-content .kick-section .subsection-header-creative h3,
        .summary-section .kick-section .subsection-header-creative h3.subsection-title,
        .summary-section .kick-section .subsection-header-creative h3,
        .kick-section .subsection-header-creative .subsection-title-wrapper > div > h3.subsection-title,
        .kick-section .subsection-header-creative .subsection-title-wrapper h3.subsection-title,
        .kick-section .subsection-header-creative .subsection-title-wrapper h3,
        .tracking-tab .subsection-header-creative h3.subsection-title,
        .tracking-tab .subsection-header-creative .subsection-title,
        .tracking-tab .subsection-header-creative h3 {
          color: #E91E63 !important;
        }
        .subsection-header-creative .subsection-title-wrapper p.subsection-subtitle-small,
        .subsection-header-creative .subsection-title-wrapper p,
        .subsection-header-creative p.subsection-subtitle-small,
        .subsection-header-creative p {
          color: #6B5B73 !important;
        }
      `;
    document.head.appendChild(style);
  }, []);

  return (
    <div className="subsection-header-creative">
      <div className="subsection-header-with-toggle">
        <div className="subsection-title-wrapper">
          <motion.div
            animate={iconAnimationProps}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
          >
            {icon}
          </motion.div>
          <div>
            <h3 className="subsection-title" style={{ color: '#E91E63' }}>{title}</h3>
            {subtitle && (
              <p className="subsection-subtitle-small">{subtitle}</p>
            )}
          </div>
        </div>
        {showToggle && onToggle && (
          <motion.button
            className="subsection-toggle-btn"
            onClick={onToggle}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label={isExpanded ? "Collapse subsection" : "Expand subsection"}
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </motion.button>
        )}
      </div>
    </div>
  );
}

export default SubsectionHeader;

